import { Injectable, signal } from '@angular/core';
import {
  AppNotification,
  NotificationCode,
  NotificationKind,
  NotificationRequest,
} from './notification.model';

export const NOTIFICATION_TIMEOUT_MS = 5000;
/** Máximo de avisos en pantalla a la vez: más no entran en la pila sin desbordar la ventana. */
export const NOTIFICATION_LIMIT = 5;

const KIND_BY_CODE: Record<NotificationCode, NotificationKind> = {
  'upload-succeeded': 'success',
  'upload-failed': 'error',
  'delete-failed': 'error',
  'download-failed': 'error',
  'finish-aborted': 'error',
  'connection-lost': 'error',
};

/** Avisos globales no bloqueantes. Recibe códigos, no textos (ver plan 4.6). */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly items = signal<AppNotification[]>([]);
  readonly notifications = this.items.asReadonly();

  private nextId = 0;
  private readonly timers = new Map<number, ReturnType<typeof setTimeout>>();

  notify({ code, params = {} }: NotificationRequest): void {
    // Con la pila llena, el aviso nuevo desplaza al más viejo (y su timer).
    const [oldest] = this.items();
    if (this.items().length >= NOTIFICATION_LIMIT) this.dismiss(oldest.id);

    const id = this.nextId++;
    this.items.update((items) => [...items, { id, code, kind: KIND_BY_CODE[code], params }]);
    this.timers.set(
      id,
      setTimeout(() => this.dismiss(id), NOTIFICATION_TIMEOUT_MS),
    );
  }

  /** ¿Hay un aviso con este código en pantalla? Sirve para no apilar avisos repetidos. */
  has(code: NotificationCode): boolean {
    return this.items().some((item) => item.code === code);
  }

  dismiss(id: number): void {
    clearTimeout(this.timers.get(id));
    this.timers.delete(id);
    this.items.update((items) => items.filter((item) => item.id !== id));
  }
}
